"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { CoverageCheck } from "./CoverageCheck";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CUSTOM = "custom";
const UNLIMITED = "unlimited";

interface PresetValueFieldProps {
  id: string;
  value: number | undefined;
  /** `undefined` = the free text field was left empty. */
  onChange: (value: number | undefined) => void;
  presets: number[];
  formatValue: (value: number) => string;
  /** Range of the free text field. */
  min: number;
  max: number;
  suffix: string;
  /** Value that stands for "unlimitiert": adds the dropdown entry and the checkbox next to it. */
  unlimitedAt?: number;
  /** A free entry above this is turned into `unlimitedAt`. */
  freeMax?: number;
  disabled?: boolean;
  hint?: string;
  /** Shown instead of the hint while "frei wählen" is selected and the text field is empty. */
  requiredMessage: string;
  error?: string;
  /** Called with `true` while "frei wählen" is selected and the text field is empty, `false` when that ends. */
  onEmptyChange?: (empty: boolean) => void;
  /** Called when the free text field is left with a 0 (see `NumericInput`). */
  onZero?: () => void;
}

/** A dropdown of preset values plus "frei wählen" (reveals a text field that has to be filled), optionally with an
 * "unlimitiert" option mirrored by a checkbox. Shared by Versicherungssumme, Kostenübernahme and Jahresfranchise. */
const PresetValueField: React.FC<PresetValueFieldProps> = ({
  id,
  value,
  onChange,
  presets,
  formatValue,
  min,
  max,
  suffix,
  unlimitedAt,
  freeMax,
  disabled,
  hint,
  requiredMessage,
  error,
  onEmptyChange,
  onZero,
}) => {
  const { t } = useTranslation();
  const hasUnlimited = unlimitedAt != null;
  const isUnlimited = hasUnlimited && value != null && value >= unlimitedAt;
  const [customMode, setCustomMode] = React.useState(() => value != null && !presets.includes(value) && !isUnlimited);

  // Restored when the customer un-ticks "unlimitiert" again.
  const lastLimited = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (value != null && !isUnlimited) lastLimited.current = value;
  }, [value, isUnlimited]);

  // "frei wählen" hands the cursor straight to the text field (after the dropdown has returned focus to its trigger).
  const customInputRef = React.useRef<HTMLInputElement>(null);
  const focusCustomRef = React.useRef(false);
  React.useEffect(() => {
    if (!customMode || !focusCustomRef.current) return;
    focusCustomRef.current = false;
    const frame = requestAnimationFrame(() => customInputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [customMode]);

  // A value outside the presets (e.g. one that arrives from a recommendation after mount) is always shown as a free entry.
  const showCustom = customMode || (value != null && !presets.includes(value) && !isUnlimited);
  const selectValue = showCustom ? CUSTOM : isUnlimited ? UNLIMITED : value != null ? String(value) : "";
  const customEmpty = showCustom && !isUnlimited && value == null;

  // Tells the parent while "frei wählen" is selected with an empty field, and clears that when this field goes away.
  // The callback is read through a ref: parents pass a new function on every render, which must not re-run the effect
  // (its cleanup would clear the flag on each re-render).
  const onEmptyChangeRef = React.useRef(onEmptyChange);
  React.useEffect(() => {
    onEmptyChangeRef.current = onEmptyChange;
  });
  React.useEffect(() => {
    if (!customEmpty) return;
    onEmptyChangeRef.current?.(true);
    return () => onEmptyChangeRef.current?.(false);
  }, [customEmpty]);

  const handleSelect = (next: string) => {
    if (next === CUSTOM) {
      if (showCustom) return;
      focusCustomRef.current = true;
      setCustomMode(true);
      // The text field opens empty instead of carrying over the preset that was selected before.
      onChange(undefined);
    } else if (next === UNLIMITED) {
      setCustomMode(false);
      onChange(unlimitedAt);
    } else {
      setCustomMode(false);
      onChange(Number(next));
    }
  };

  const handleUnlimitedToggle = (checked: boolean) => {
    if (checked) {
      onChange(unlimitedAt);
      return;
    }
    const restore = lastLimited.current ?? presets[0];
    setCustomMode(!presets.includes(restore));
    onChange(restore);
  };

  const handleFreeEntry = (entered: number | undefined) => {
    onChange(entered != null && hasUnlimited && freeMax != null && entered > freeMax ? unlimitedAt : entered);
  };

  const message = error ?? (customEmpty ? requiredMessage : undefined);
  const describedBy = message || hint ? `${id}-message` : undefined;

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="w-full sm:w-56">
          <Select value={selectValue} onValueChange={handleSelect} disabled={disabled || (isUnlimited && !showCustom)}>
            <SelectTrigger id={id} aria-invalid={!!message} aria-describedby={describedBy}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {presets.map((preset) => (
                <SelectItem key={preset} value={String(preset)}>
                  {formatValue(preset)}
                </SelectItem>
              ))}
              {hasUnlimited && <SelectItem value={UNLIMITED}>{t("coverages.unlimited")}</SelectItem>}
              <SelectItem value={CUSTOM}>{t("coverages.custom")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {showCustom && (
          <div className="w-full sm:w-40">
            {isUnlimited ? (
              <div className="relative">
                <Input id={`${id}-custom`} value="–" disabled readOnly className="pr-12" aria-label={t("coverages.custom")} />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{suffix}</span>
              </div>
            ) : (
              <NumericInput
                ref={customInputRef}
                id={`${id}-custom`}
                aria-label={t("coverages.custom")}
                commitOn="blur"
                onZero={onZero}
                value={value}
                onChange={handleFreeEntry}
                min={min}
                max={max}
                suffix={suffix}
                disabled={disabled}
                error={!!message}
                aria-invalid={!!message}
                aria-describedby={describedBy}
                className="w-full"
              />
            )}
          </div>
        )}
        {hasUnlimited && (
          <CoverageCheck checked={isUnlimited} label={t("coverages.unlimited")} disabled={disabled} onChange={handleUnlimitedToggle}>
            <span className="text-sm">{t("coverages.unlimited")}</span>
          </CoverageCheck>
        )}
      </div>
      {message ? (
        <p id={`${id}-message`} role="alert" className="text-xs text-destructive">{message}</p>
      ) : (
        hint && <p id={`${id}-message`} className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
};

export default PresetValueField;
